import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { can, type MeResponse, type Permission, type Viewer } from '@sparkery/contracts';
import { writeDevIdentity } from './api.ts';
import { usePortalAuth } from './auth.tsx';

/**
 * The viewer, for the whole app.
 *
 * Loaded once before the router renders, so no route ever flickers through an
 * unauthenticated state and no guard has to handle "still loading".
 *
 * The permissions come from the SERVER, never derived from a token client-side — so a
 * client-side check can never disagree with the server-side one. The client checks are
 * for what to *show*; the server's guard is what actually protects anything.
 */
export interface ViewerContextValue {
  viewer: Viewer;
  provider: string;
  can: (...permissions: Permission[]) => boolean;
  /** Ends an impersonated session. Dev implementation clears the header. */
  quitImpersonation: () => void;
  signOut: () => void;
}

const ViewerContext = createContext<ViewerContextValue | null>(null);

export function ViewerProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const quitImpersonation = useCallback(() => {
    // Automated test auth carries impersonation in a header, so clearing it and reloading
    // is the whole operation. WorkOS impersonation ends the server-side session here.
    const identity = { user: 'admin' as const };
    writeDevIdentity(identity);
    window.location.assign('/');
  }, []);

  const auth = usePortalAuth();

  const signOut = useCallback(() => {
    // With a provider, ending its session is the operation — clearing local state alone
    // would leave the viewer signed in and straight back where they were on reload.
    if (auth.signOut) {
      auth.signOut();
      return;
    }
    writeDevIdentity({});
    window.location.assign('/');
  }, [auth]);

  const value = useMemo<ViewerContextValue>(
    () => ({
      viewer: me.viewer,
      provider: me.provider,
      can: (...permissions: Permission[]) => can(me.viewer, ...permissions),
      quitImpersonation,
      signOut,
    }),
    [me, quitImpersonation, signOut],
  );

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

export function useViewer(): ViewerContextValue {
  const value = useContext(ViewerContext);
  if (!value) throw new Error('useViewer must be used inside a ViewerProvider');
  return value;
}
